import {
  metricDayPl,
  type BalanceValuationRow,
  type PortfolioBalanceAnchor,
  type PortfolioBalancePoint,
} from "./types";

export interface PortfolioBalanceRangeRoi {
  startDate: string;
  endDate: string;
  startNlv: number;
  endNlv: number;
  returnPct: number;
}

export type PortfolioBalanceChartCadence = "daily" | "weekly" | "monthly";

function compareIsoDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function portfolioBalanceBucketKey(
  date: string,
  cadence: PortfolioBalanceChartCadence
): string {
  if (cadence === "daily") return date;
  if (cadence === "monthly") return date.slice(0, 7);

  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  const isoWeekday = parsed.getUTCDay() || 7;
  parsed.setUTCDate(parsed.getUTCDate() - isoWeekday + 1);
  return parsed.toISOString().slice(0, 10);
}

/**
 * Reduces an NLV curve to one period-end account value per selected bucket.
 *
 * NLV is a point-in-time balance, so values are never summed. NLV change is
 * summed but withheld when a later manual anchor reset occurs inside the bucket.
 * Portfolio P/L is aggregated independently from canonical day-P/L and stays
 * usable for gain/loss coloring across that reset.
 */
export function bucketPortfolioBalancePoints(
  points: readonly PortfolioBalancePoint[],
  cadence: PortfolioBalanceChartCadence
): PortfolioBalancePoint[] {
  const ordered = [...points].sort((a, b) =>
    compareIsoDates(a.asOfDate, b.asOfDate)
  );
  if (cadence === "daily") return ordered;

  const buckets = new Map<
    string,
    {
      last: PortfolioBalancePoint;
      change: number;
      hasChange: boolean;
      containsReset: boolean;
      profitLossChange: number;
      hasProfitLossChange: boolean;
    }
  >();

  ordered.forEach((point, index) => {
    const key = portfolioBalanceBucketKey(point.asOfDate, cadence);
    const bucket = buckets.get(key) ?? {
      last: point,
      change: 0,
      hasChange: false,
      containsReset: false,
      profitLossChange: 0,
      hasProfitLossChange: false,
    };
    if (point.dailyChange == null) {
      // The first visible point is the normal range baseline. Any later null
      // is an anchor reset (or a broken change chain), so the bucket change
      // cannot be stated safely.
      if (index > 0) bucket.containsReset = true;
    } else {
      bucket.change += point.dailyChange;
      bucket.hasChange = true;
    }
    if (point.profitLossChange != null) {
      bucket.profitLossChange += point.profitLossChange;
      bucket.hasProfitLossChange = true;
    }
    bucket.last = point;
    buckets.set(key, bucket);
  });

  return [...buckets.values()].map((bucket) => ({
    ...bucket.last,
    dailyChange:
      bucket.containsReset || !bucket.hasChange ? null : bucket.change,
    profitLossChange: bucket.hasProfitLossChange
      ? bucket.profitLossChange
      : null,
  }));
}

export function resolveBalanceAnchorRow<T extends BalanceValuationRow>(
  valuationSeries: readonly T[] | null,
  requestedDate: string
): T | null {
  if (!valuationSeries || valuationSeries.length === 0 || !requestedDate) {
    return null;
  }
  let resolved: T | null = null;
  for (const row of [...valuationSeries].sort((a, b) =>
    compareIsoDates(a.asOfDate, b.asOfDate)
  )) {
    if (compareIsoDates(row.asOfDate, requestedDate) > 0) break;
    resolved = row;
  }
  return resolved;
}

export function derivePortfolioBalanceSeries(
  valuationSeries: readonly BalanceValuationRow[] | null,
  anchors: readonly PortfolioBalanceAnchor[] | null
): PortfolioBalancePoint[] {
  type ResolvedBalanceAnchor = {
    anchor: PortfolioBalanceAnchor;
    effectiveDate: string;
    anchorRow: BalanceValuationRow;
  };
  const rows = [...(valuationSeries ?? [])].sort((a, b) =>
    compareIsoDates(a.asOfDate, b.asOfDate)
  );
  const sortedAnchors = [...(anchors ?? [])]
    .filter((anchor) => Number.isFinite(anchor.startingBalance))
    .map<ResolvedBalanceAnchor | null>((anchor) => {
      const anchorRow = resolveBalanceAnchorRow(rows, anchor.startingBalanceDate);
      const normalizedAnchor: PortfolioBalanceAnchor = {
        ...anchor,
        balanceSource:
          anchor.balanceSource === "schwab" ? "schwab" : "manual",
      };
      return anchorRow
        ? {
            anchor: normalizedAnchor,
            effectiveDate: anchorRow.asOfDate,
            anchorRow,
          }
        : null;
    })
    .filter((item): item is ResolvedBalanceAnchor => item != null)
    .sort((a, b) =>
      compareIsoDates(a.effectiveDate, b.effectiveDate)
    );
  if (rows.length === 0 || sortedAnchors.length === 0) return [];

  const points: PortfolioBalancePoint[] = [];
  // The earliest anchor is active from the very first row so history before
  // it is back-cast with the same formula (realized delta goes negative).
  // The curve is continuous through that anchor's own date, so no daily-
  // change reset happens there — only at later re-anchors, where the step
  // is real (deposits/withdrawals/untracked items).
  let anchorIndex = 0;
  let activeAnchor: PortfolioBalanceAnchor = sortedAnchors[0].anchor;
  let activeAnchorRow: BalanceValuationRow = sortedAnchors[0].anchorRow;
  let activeAnchorDate: string = sortedAnchors[0].effectiveDate;
  let activeBalanceSource: "manual" | "schwab" =
    sortedAnchors[0].anchor.balanceSource === "schwab" ? "schwab" : "manual";
  let previousNlv: number | null = null;
  let previousProfitLoss: number | null = null;

  for (const row of rows) {
    while (
      anchorIndex + 1 < sortedAnchors.length &&
      compareIsoDates(
        sortedAnchors[anchorIndex + 1].effectiveDate,
        row.asOfDate
      ) <= 0
    ) {
      anchorIndex++;
      const candidate = sortedAnchors[anchorIndex];
      activeAnchor = candidate.anchor;
      activeAnchorRow = candidate.anchorRow;
      activeBalanceSource =
        candidate.anchor.balanceSource === "schwab" ? "schwab" : "manual";
      // Routine broker observations update the curve's factual level without
      // becoming cash-flow/re-anchor markers. Manual entries retain the old
      // explicit re-anchor semantics.
      if (activeBalanceSource === "manual") {
        activeAnchorDate = candidate.effectiveDate;
        previousNlv = null;
      }
    }

    const realizedDelta =
      row.realizedPlCumulative - activeAnchorRow.realizedPlCumulative;
    const projectedNlv =
      activeAnchor.startingBalance + realizedDelta + row.unrealizedPl;
    const netLiquidatingValue =
      activeBalanceSource === "schwab" &&
      row.asOfDate === sortedAnchors[anchorIndex].effectiveDate &&
      activeAnchor.observedNlv != null &&
      Number.isFinite(activeAnchor.observedNlv)
        ? activeAnchor.observedNlv
        : projectedNlv;
    const profitLoss = row.realizedPlCumulative + row.unrealizedPl;
    const fallbackProfitLossChange =
      previousProfitLoss == null ? null : profitLoss - previousProfitLoss;
    points.push({
      asOfDate: row.asOfDate,
      netLiquidatingValue,
      dailyChange:
        previousNlv == null ? null : netLiquidatingValue - previousNlv,
      profitLossChange: metricDayPl(row, fallbackProfitLossChange),
      balanceSource: activeBalanceSource,
      anchorDate: activeAnchorDate,
    });
    previousNlv = netLiquidatingValue;
    previousProfitLoss = profitLoss;
  }

  return points;
}

/**
 * Simple endpoint ROI for the visible NLV range.
 *
 * The derived points already apply the correct source precedence: a saved
 * broker NetLiq anchor overrides the projection on its effective date, while
 * dates without an anchor remain projected from the active anchor plus P/L.
 * Computing from the visible endpoints therefore uses an exact broker value
 * whenever the user supplied one and the projected value otherwise.
 */
export function calculatePortfolioBalanceRangeRoi(
  points: readonly PortfolioBalancePoint[] | null
): PortfolioBalanceRangeRoi | null {
  const ordered = [...(points ?? [])]
    .filter(
      (point) =>
        !!point.asOfDate && Number.isFinite(point.netLiquidatingValue)
    )
    .sort((a, b) => compareIsoDates(a.asOfDate, b.asOfDate));
  if (ordered.length < 2) return null;

  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  if (!(first.netLiquidatingValue > 0)) return null;

  return {
    startDate: first.asOfDate,
    endDate: last.asOfDate,
    startNlv: first.netLiquidatingValue,
    endNlv: last.netLiquidatingValue,
    returnPct:
      ((last.netLiquidatingValue - first.netLiquidatingValue) /
        first.netLiquidatingValue) *
      100,
  };
}
